// packages/web-app/src/utils/storageUtils.ts
import { ContextService } from '@/services/contextService';

/**
 * Keys that should persist across page refreshes (the `beforeunload` wipe path)
 * but still be cleared on explicit sign-out. Add any new "stays on the device
 * between sessions" data here — e.g., liked candidates, future filter prefs.
 */
const PERSISTENT_KEYS: ReadonlySet<string> = new Set([
  'votemate_liked_candidates',
  'votemate_measure_selections',
  'votemate_contest_filters',
  'votemate_election_filters',
  'votemate_expanded_contests',
  // Research-link tags (campaign / participant) — non-PII opaque tokens. Stored
  // in localStorage so a participant who reopens the bare site (no link) keeps
  // their study context instead of starting blank. Listed here so the
  // beforeunload wipe preserves them across a tab close, while explicit sign-out
  // still clears them. The research address is PII and lives in
  // SESSION_PERSISTENT_KEYS instead.
  'votemate_research_campaign_code',
  'votemate_research_participant_id',
  // A signed-in user's on-device DRAFT ballot picks (see draftSelectionsStore).
  // Listed here so drafts survive the beforeunload refresh wipe; still cleared
  // on explicit sign-out.
  'votemate_draft_selections',
  // Same idea for measure votes (see draftMeasureSelectionsStore).
  'votemate_draft_measure_selections',
]);

/**
 * sessionStorage keys that should survive an in-session refresh (the
 * `beforeunload` wipe below) but are naturally cleared by the browser on tab
 * close and explicitly wiped on sign-out. The cached addresses (the guest's
 * entered address and the research-link `?address=` pre-fill hint) and the
 * guest's in-progress chat live here so they are erased when the browser is
 * closed (a privacy commitment) yet don't vanish on every refresh.
 */
const SESSION_PERSISTENT_KEYS: ReadonlySet<string> = new Set([
  'votemate_guest_address',
  'votemate_research_address',
  // Guest's in-progress chat. Guests have no server-side conversation to
  // reload, so their chat is kept on-device and restored when they return from
  // Settings/Help. Session-scoped (survives refresh, cleared on tab close and
  // on explicit sign-out) to match the guest address's privacy behavior.
  'votemate_guest_chat',
  // The keys that resume the guest's conversations (services/guestKey.ts); a
  // restored guest chat without its key would start over on the next turn.
  'votemate_guest_keys',
  // Membership checkout: the chosen plan and an unconfirmed redirect payment
  // (components/membership/checkoutPlanStorage.ts) must survive the trip to the
  // payment page and back.
  'votemate_membership_checkout',
  'votemate_membership_pending_session',
]);

const isVotemateKey = (key: string): boolean =>
  key.startsWith('sources-expanded-') || key.startsWith('votemate_');

/**
 * Clear VoteMate-specific client storage *except* keys in `PERSISTENT_KEYS`.
 * Called on browser window/tab close (`beforeunload`) so refresh preserves
 * persistent data while transient session data is wiped.
 */
export const clearVoteMateStorage = (): void => {
  try {
    // Clear conversation context from sessionStorage
    ContextService.clearContext();

    // Clear VoteMate-specific localStorage items, skipping persistent ones
    Object.keys(localStorage)
      .filter((key) => isVotemateKey(key) && !PERSISTENT_KEYS.has(key))
      .forEach((key) => localStorage.removeItem(key));

    // Clear VoteMate-specific sessionStorage items, skipping session-persistent
    // ones (e.g. the guest address) so an in-session refresh keeps them; the
    // browser clears all sessionStorage on tab close regardless.
    Object.keys(sessionStorage)
      .filter((key) => isVotemateKey(key) && !SESSION_PERSISTENT_KEYS.has(key))
      .forEach((key) => sessionStorage.removeItem(key));

    console.log('VoteMate storage cleared successfully');
  } catch (error) {
    console.warn('Failed to clear VoteMate storage:', error);
  }
};

/**
 * Clear *all* VoteMate storage — including `PERSISTENT_KEYS` — while preserving
 * the logout event for cross-tab synchronization. Called from the explicit
 * sign-out flow so per-device data (liked candidates, filters, etc.) is wiped
 * when the user logs out, even though it survives ordinary refreshes.
 */
export const clearVoteMateStorageOnLogout = (): void => {
  try {
    // Store logout event temporarily
    const logoutEvent = localStorage.getItem('votemate_logout_event');

    // Bulk clear (skips persistent keys), then explicitly clear persistent ones
    // (both the localStorage-persistent and sessionStorage-persistent sets).
    clearVoteMateStorage();
    PERSISTENT_KEYS.forEach((key) => localStorage.removeItem(key));
    SESSION_PERSISTENT_KEYS.forEach((key) => sessionStorage.removeItem(key));

    // Restore logout event for tab synchronization
    if (logoutEvent) {
      localStorage.setItem('votemate_logout_event', logoutEvent);
    }

    console.log('VoteMate storage cleared on logout');
  } catch (error) {
    console.warn('Failed to clear VoteMate storage on logout:', error);
  }
};
